import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Store, Loader2, Pencil, Trash2, ExternalLink, ShoppingBag, Plus, Eye } from 'lucide-react';
import { toast } from 'sonner';
import MarketplaceBrowser from '@/components/marketplace/MarketplaceBrowser';
import SellSiteDialog from '@/components/marketplace/SellSiteDialog';
import ListingThumb from '@/components/marketplace/ListingThumb';
import { myListings, myPurchases, removeListing, LISTING_STATUS } from '@/lib/marketplace';
import { categoryLabel, usd } from '@/config/earnings';

// ─── Dashboard → Marketplace ──────────────────────────────────────────────────
// Browse what is for sale, manage what you are selling, and find what you have
// bought — three questions, three tabs.

const TABS = [
  { id: 'browse', label: 'Browse' },
  { id: 'selling', label: 'My listings' },
  { id: 'bought', label: 'Purchases' },
];

function MyListings({ onSell }) {
  const [listings, setListings] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    myListings()
      .then(({ listings: rows }) => setListings(rows || []))
      .catch((err) => { toast.error(err.message); setListings([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (listing) => {
    if (!window.confirm(`Take “${listing.title}” off the marketplace?`)) return;
    try {
      await removeListing(listing.id);
      setListings(cur => cur.filter(l => l.id !== listing.id));
      toast.success('Listing removed.');
    } catch (err) { toast.error(err.message); }
  };

  if (listings === null) {
    return <div className="flex items-center gap-2 text-sm text-zinc-500 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading your listings…</div>;
  }

  return (
    <div>
      {listings.length === 0 ? (
        <div className="border border-dashed border-zinc-800 rounded-2xl p-10 text-center">
          <Store className="w-9 h-9 text-zinc-700 mx-auto mb-3" />
          <p className="text-white font-semibold">You are not selling anything yet</p>
          <p className="text-zinc-500 text-sm mt-1">List a site you built and earn every time someone buys it.</p>
          <button onClick={onSell} className="mt-5 h-10 px-5 rounded-full bg-orange-500 hover:bg-orange-400 text-ink text-sm font-semibold">Sell a site</button>
        </div>
      ) : (
        <div className="space-y-3">
          {listings.map(l => {
            const status = LISTING_STATUS[l.status] || LISTING_STATUS.pending;
            return (
              <div key={l.id} className="rounded-2xl border border-zinc-800 bg-zinc-900/70 overflow-hidden flex flex-col sm:flex-row">
                <ListingThumb listingId={l.id} title={l.title} iconSvg={l.icon_svg} className="h-36 sm:h-auto sm:w-56 shrink-0" />
                <div className="flex-1 min-w-0 p-4 flex flex-col gap-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-white font-semibold truncate">{l.title}</p>
                      <p className="text-xs text-zinc-500">{categoryLabel(l.category)} · {l.license === 'exclusive' ? 'One buyer' : 'Any number of buyers'}</p>
                    </div>
                    <span className={`text-[11px] font-semibold rounded-full border px-2.5 py-0.5 ${status.tone}`}>{status.label}</span>
                  </div>
                  {l.status === 'rejected' && l.rejection_reason && (
                    <p className="text-xs text-red-300/90 leading-relaxed">{l.rejection_reason} Edit the listing to send it for review again.</p>
                  )}
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-400">
                    <span>Price <span className="text-white font-semibold">{usd(l.price_usd)}</span></span>
                    <span>{l.sales_count} sold</span>
                    <span>{l.views} views</span>
                    <span>Earned <span className="text-emerald-400 font-semibold">{usd(l.earned_usd)}</span></span>
                  </div>
                  <div className="mt-auto flex flex-wrap gap-2 pt-1">
                    <Link to={`/marketplace/${l.id}`} className="h-8 px-3 rounded-full border border-zinc-700 text-xs font-semibold text-zinc-300 hover:text-white inline-flex items-center gap-1.5">
                      <Eye className="w-3.5 h-3.5" /> View
                    </Link>
                    {l.status !== 'sold' && (
                      <button onClick={() => setEditing(l)} className="h-8 px-3 rounded-full border border-zinc-700 text-xs font-semibold text-zinc-300 hover:text-white inline-flex items-center gap-1.5">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                    )}
                    <button onClick={() => remove(l)} className="h-8 px-3 rounded-full border border-red-500/30 text-xs font-semibold text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1.5">
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <SellSiteDialog open={!!editing} listing={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function Purchases({ navigate }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    myPurchases().then(({ purchases }) => setRows(purchases || [])).catch(() => setRows([]));
  }, []);

  if (rows === null) return <div className="flex items-center gap-2 text-sm text-zinc-500 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!rows.length) {
    return (
      <div className="border border-dashed border-zinc-800 rounded-2xl p-10 text-center">
        <ShoppingBag className="w-9 h-9 text-zinc-700 mx-auto mb-3" />
        <p className="text-zinc-400 text-sm">Sites you buy on the marketplace show up here, and in My sites.</p>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-zinc-800 rounded-2xl border border-zinc-800 bg-zinc-900/70">
      {rows.map(p => (
        <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
          <span className="min-w-0">
            <span className="block text-sm text-white truncate">{p.title || 'Marketplace site'}</span>
            <span className="block text-[11px] text-zinc-500">
              {new Date(p.created_at).toLocaleDateString()} · {p.currency} {Number(p.amount).toLocaleString()}
            </span>
          </span>
          {p.website_id && (
            <button onClick={() => navigate(`/build/${p.website_id}`)}
              className="shrink-0 h-8 px-3 rounded-full bg-white text-zinc-950 text-xs font-semibold inline-flex items-center gap-1.5">
              Open <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function MarketplaceSection({ navigate, initialTab = 'browse' }) {
  const [tab, setTab] = useState(TABS.some(t => t.id === initialTab) ? initialTab : 'browse');
  const [selling, setSelling] = useState(false);
  const [listingsKey, setListingsKey] = useState(0);

  return (
    <div className="p-4 sm:p-6 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-white">Marketplace</h2>
          <p className="text-zinc-500 text-sm mt-1">Buy a ready-made site, or sell the ones you build.</p>
        </div>
        <button onClick={() => setSelling(true)}
          className="h-10 px-4 rounded-full bg-orange-500 hover:bg-orange-400 text-ink text-sm font-semibold inline-flex items-center gap-1.5 self-start sm:self-auto">
          <Plus className="w-4 h-4" /> Sell a site
        </button>
      </div>

      <div className="flex gap-1 mb-6 border-b border-zinc-800" role="tablist">
        {TABS.map(t => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`px-4 h-10 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-orange-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'browse' && <MarketplaceBrowser />}
      {tab === 'selling' && <MyListings key={listingsKey} onSell={() => setSelling(true)} />}
      {tab === 'bought' && <Purchases navigate={navigate} />}

      <SellSiteDialog
        open={selling}
        onClose={() => setSelling(false)}
        onSaved={() => { setTab('selling'); setListingsKey(k => k + 1); }}
      />
    </div>
  );
}
